import React, { useEffect, useRef, useState } from 'react';

/**
 * A living metric card: an animated count-up number, an accent rail, an icon,
 * and (when clickable) a hover-revealed "drill in" cue. Each card is a doorway
 * to the list/detail behind the metric.
 */

export interface StatCardProps {
  label: string;
  value: number;
  icon: string;
  accent: string;       // base accent hex
  hint?: string;        // small sub-line (e.g. "3 active")
  drillLabel?: string;  // hover CTA, e.g. "View organizations"
  onClick?: () => void;
  format?: (n: number) => string;
}

/** Ease-out count-up animation for the value. */
function useCountUp(target: number, duration = 650): number {
  const [v, setV] = useState(0);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(from + (target - from) * eased));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);
  return v;
}

export function StatCard({ label, value, icon, accent, hint, drillLabel, onClick, format }: StatCardProps) {
  const [hover, setHover] = useState(false);
  const shown = useCountUp(value);
  const clickable = !!onClick;
  const display = format ? format(shown) : shown.toLocaleString();

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      aria-label={clickable ? `${label}: ${display}. ${drillLabel || 'Open'}` : `${label}: ${display}`}
      style={{
        ...styles.card,
        cursor: clickable ? 'pointer' : 'default',
        transform: hover && clickable ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hover && clickable
          ? `0 14px 32px -14px ${accent}70, 0 2px 8px rgba(16,24,40,0.06)`
          : '0 1px 3px rgba(16,24,40,0.05)',
        borderColor: hover && clickable ? `${accent}44` : '#e8eaf0',
      }}
    >
      <span aria-hidden style={{ ...styles.rail, background: `linear-gradient(180deg, ${accent}, ${accent}88)` }} />
      <div style={styles.topRow}>
        <span style={{ ...styles.iconChip, background: `${accent}14`, color: accent }}>{icon}</span>
        {clickable && (
          <span style={{ ...styles.drill, opacity: hover ? 1 : 0, color: accent }}>
            {drillLabel || 'Open'} <span style={{ transition: 'transform .2s', display: 'inline-block', transform: hover ? 'translateX(3px)' : 'none' }}>→</span>
          </span>
        )}
      </div>
      <div style={{ ...styles.value, color: '#0b1120' }}>{display}</div>
      <div style={styles.label}>{label}</div>
      {hint && <div style={{ ...styles.hint, color: accent }}>{hint}</div>}
    </button>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'left',
    padding: '18px 18px 16px 22px',
    borderRadius: 16,
    border: '1px solid #e8eaf0',
    background: '#fff',
    transition: 'transform 0.18s cubic-bezier(.2,.8,.2,1), box-shadow 0.18s ease, border-color 0.18s ease',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  rail: { position: 'absolute', top: 10, bottom: 10, left: 0, width: 4, borderRadius: 999 },
  topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  iconChip: { width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', fontSize: '1.05rem' },
  drill: { fontSize: '0.72rem', fontWeight: 700, transition: 'opacity 0.2s ease', whiteSpace: 'nowrap' },
  value: { fontSize: '2.1rem', fontWeight: 800, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' },
  label: { color: '#6b7280', fontSize: '0.82rem', fontWeight: 600, marginTop: 4 },
  hint: { fontSize: '0.74rem', marginTop: 3, fontWeight: 700 },
};
